import React, { useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useAriRoomRateActivity, useAriRoomRateState } from '~/hooks/apis/ari/useAriRoomRateLookup';

import {
  AriRoomRateActivityParams,
  AriRoomRateActivityRow,
  AriRoomRateStateParams,
  AriSourceService,
} from '~/services/ReportingService';

import AriActivityTable from './AriActivityTable';
import AriPayloadDialog from './AriPayloadDialog';
import AriStateTable, { anyDurationPriced } from './AriStateTable';
import {
  ROOM_TYPE_GRAIN_INVENTORY_SOURCE,
  ROOM_TYPE_GRAIN_MESSAGE_NAMES,
  SEARCH_BY_LABELS,
  SOURCE_SERVICES,
  SearchBy,
  activityWindowDays,
} from './constants';

type ResultsTab = 'activity' | 'state';

interface SubmittedSearch {
  searchBy: SearchBy;
  identifier: string;
  stayDate: string;
  createdDateFrom: string;
  createdDateTo: string;
  sourceService: AriSourceService | '';
}

function toIdentifierParams(search: SubmittedSearch) {
  return {
    roomRateId: search.searchBy === 'room_rate' ? search.identifier : undefined,
    roomTypeId: search.searchBy === 'room_type' ? search.identifier : undefined,
    ratePlanId: search.searchBy === 'rate_plan' ? search.identifier : undefined,
  };
}

export default function AriSearchPage() {
  const [searchBy, setSearchBy] = useState<SearchBy>('room_rate');
  const [identifier, setIdentifier] = useState('');
  const [stayDate, setStayDate] = useState('');
  const [createdDateFrom, setCreatedDateFrom] = useState(
    dayjs()
      .subtract(activityWindowDays('room_rate') - 1, 'day')
      .format('YYYY-MM-DD'),
  );
  const [createdDateTo, setCreatedDateTo] = useState(dayjs().format('YYYY-MM-DD'));
  const [sourceService, setSourceService] = useState<AriSourceService | ''>('');

  const [submitted, setSubmitted] = useState<SubmittedSearch | null>(null);
  const [resultsTab, setResultsTab] = useState<ResultsTab>('activity');
  const [payloadRow, setPayloadRow] = useState<AriRoomRateActivityRow | null>(null);

  const isSingleDaySearch = activityWindowDays(searchBy) === 1;

  const validationError = useMemo(() => {
    if (!identifier.trim()) {
      return `${SEARCH_BY_LABELS[searchBy]} is required`;
    }
    if (!stayDate) {
      return 'Stay date is required';
    }
    const from = dayjs(createdDateFrom);
    const to = dayjs(createdDateTo);
    if (!isSingleDaySearch && from.isAfter(to)) {
      return 'Received from must not be after received to';
    }
    const windowDays = activityWindowDays(searchBy);
    if (to.diff(from, 'day') + 1 > windowDays) {
      return searchBy === 'room_rate'
        ? `Received date range must span at most ${windowDays} days`
        : `A ${SEARCH_BY_LABELS[searchBy].toLowerCase()} search scans every room rate underneath, so it is limited to a single received date. Search by room rate ID for up to ${activityWindowDays('room_rate')} days.`;
    }
    return null;
  }, [identifier, searchBy, stayDate, createdDateFrom, createdDateTo, isSingleDaySearch]);

  const activityParams: AriRoomRateActivityParams | undefined = submitted
    ? {
        ...toIdentifierParams(submitted),
        stayDate: submitted.stayDate,
        createdDateFrom: submitted.createdDateFrom,
        createdDateTo: submitted.createdDateTo,
        sourceService: submitted.sourceService || undefined,
      }
    : undefined;

  const stateParams: AriRoomRateStateParams | undefined = submitted
    ? {
        ...toIdentifierParams(submitted),
        stayDate: submitted.stayDate,
        sourceService: submitted.sourceService || undefined,
      }
    : undefined;

  const activityQuery = useAriRoomRateActivity(activityParams);
  const stateQuery = useAriRoomRateState(stateParams);

  const activityRows = activityQuery.data?.data ?? [];
  const stateRows = stateQuery.data?.data ?? [];

  const isLoading = resultsTab === 'activity' ? activityQuery.isFetching : stateQuery.isFetching;
  const error = resultsTab === 'activity' ? activityQuery.error : stateQuery.error;
  const hasSearched = !!submitted;

  // A room-rate search cannot see Synxis room-type-grain inventory, so offer to
  // re-run by the room type these rows already carry rather than hide the gap.
  const roomTypeIdFromResults = activityRows.find((row) => row.room_type_id)?.room_type_id ?? null;
  const showRoomTypeHint =
    hasSearched &&
    submitted?.searchBy === 'room_rate' &&
    activityRows.length > 0 &&
    activityRows.some((row) => row.source_service === ROOM_TYPE_GRAIN_INVENTORY_SOURCE) &&
    !activityRows.some((row) => ROOM_TYPE_GRAIN_MESSAGE_NAMES.includes(row.message_name)) &&
    !!roomTypeIdFromResults;

  function setReceivedOn(receivedOn: string) {
    setCreatedDateFrom(receivedOn);
    setCreatedDateTo(receivedOn);
  }

  function handleSearchByChange(nextSearchBy: SearchBy) {
    setSearchBy(nextSearchBy);
    const windowDays = activityWindowDays(nextSearchBy);
    setCreatedDateFrom(
      dayjs(createdDateTo)
        .subtract(windowDays - 1, 'day')
        .format('YYYY-MM-DD'),
    );
  }

  function handleSearch() {
    if (validationError) {
      return;
    }
    setSubmitted({ searchBy, identifier: identifier.trim(), stayDate, createdDateFrom, createdDateTo, sourceService });
  }

  function handleSearchByRoomType(roomTypeId: string) {
    const windowDays = activityWindowDays('room_type');
    const clampedFrom = dayjs(createdDateTo)
      .subtract(windowDays - 1, 'day')
      .format('YYYY-MM-DD');
    setSearchBy('room_type');
    setIdentifier(roomTypeId);
    setCreatedDateFrom(clampedFrom);
    setSubmitted({
      searchBy: 'room_type',
      identifier: roomTypeId,
      stayDate,
      createdDateFrom: clampedFrom,
      createdDateTo,
      sourceService,
    });
  }

  return (
    <Box>
      <PageSubheader title="ARI Search">
        <Typography variant="body2" color="text.secondary">
          What did we receive from the channel manager about this room rate, for this stay date?
        </Typography>
      </PageSubheader>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="ari-search-by-label">Search by</InputLabel>
              <Select
                labelId="ari-search-by-label"
                label="Search by"
                value={searchBy}
                onChange={(event) => handleSearchByChange(event.target.value)}
              >
                {(Object.keys(SEARCH_BY_LABELS) as Array<SearchBy>).map((key) => (
                  <MenuItem key={key} value={key}>
                    {SEARCH_BY_LABELS[key]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 8, md: 5 }}>
            <TextField
              fullWidth
              size="small"
              label={SEARCH_BY_LABELS[searchBy]}
              placeholder="UUID"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Stay date"
              value={stayDate}
              onChange={(event) => setStayDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="The night being stayed, not the date we received the push"
            />
          </Grid>

          {isSingleDaySearch ? (
            <Grid size={{ xs: 12, sm: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Received on"
                value={createdDateTo}
                onChange={(event) => setReceivedOn(event.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText={`A ${SEARCH_BY_LABELS[searchBy].toLowerCase()} search covers one day at a time`}
              />
            </Grid>
          ) : (
            <>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Received from"
                  value={createdDateFrom}
                  onChange={(event) => setCreatedDateFrom(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Received to"
                  value={createdDateTo}
                  onChange={(event) => setCreatedDateTo(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText={`Up to ${activityWindowDays(searchBy)} days`}
                />
              </Grid>
            </>
          )}

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="ari-source-service-label">Channel manager</InputLabel>
              <Select
                labelId="ari-source-service-label"
                label="Channel manager"
                value={sourceService}
                onChange={(event) => setSourceService(event.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {SOURCE_SERVICES.map((service) => (
                  <MenuItem key={service} value={service}>
                    {service}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={!!validationError}
            >
              Search
            </Button>
          </Grid>
        </Grid>

        <Typography
          variant="caption"
          color={validationError ? 'error' : 'text.secondary'}
          component="div"
          sx={{ mt: 2 }}
        >
          {validationError ?? 'Ready to search.'}
        </Typography>
      </Paper>

      {showRoomTypeHint && roomTypeIdFromResults && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Inventory pushes are not shown</AlertTitle>
          Synxis sends inventory on <code>OTA_HotelInvCountNotifRQ</code>, which is room-type grain and carries no rate
          plan — so those rows have no room rate ID and cannot appear in a room-rate search. Derbysoft and SiteMinder
          are unaffected: they carry inventory on the rate row itself.{' '}
          <Link component="button" type="button" onClick={() => handleSearchByRoomType(roomTypeIdFromResults)}>
            Search by room type instead
          </Link>{' '}
          to include them.
        </Alert>
      )}

      {!!error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Query failed</AlertTitle>
          {error.message}
        </Alert>
      )}

      {hasSearched && (
        <Paper variant="outlined">
          <Tabs
            value={resultsTab}
            onChange={(_event, value: ResultsTab) => setResultsTab(value)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="activity" label={`Push history (${activityRows.length})`} />
            <Tab value="state" label={`Current state (${stateRows.length})`} />
          </Tabs>

          {isLoading && (
            <Stack direction="row" spacing={1.5} alignItems="center" p={3}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Querying BigQuery&hellip;
              </Typography>
            </Stack>
          )}

          {!isLoading && resultsTab === 'activity' && (
            <>
              <Box px={2} pt={1.5}>
                <Typography variant="caption" color="text.secondary">
                  Every ARI message we received in the window whose stay range covers the stay date, newest first.
                </Typography>
              </Box>
              {activityRows.length === 0 ? (
                <Box p={3}>
                  <Typography variant="body2" color="text.secondary">
                    No ARI received for this {SEARCH_BY_LABELS[submitted?.searchBy ?? 'room_rate'].toLowerCase()} in
                    this window. That is a finding in itself — a live rate with no pushes usually means the hotel has
                    not connected it on the channel manager side.
                  </Typography>
                </Box>
              ) : (
                <AriActivityTable rows={activityRows} onViewPayload={setPayloadRow} />
              )}
            </>
          )}

          {!isLoading && resultsTab === 'state' && (
            <>
              {anyDurationPriced(stateRows) && (
                <Alert severity="info" sx={{ m: 2, mb: 0 }}>
                  <AlertTitle>Some rates below are stay totals, not nightly prices</AlertTitle>
                  This product is duration-priced, so the provider sends one total per length of stay and there is no
                  single nightly rate. Each LOS row carries the total for a stay of that many nights — a 3-night row is
                  roughly three times the 1-night row. Compare against the room-rate inventory page per band, not per
                  night.
                </Alert>
              )}
              <Box px={2} pt={1.5}>
                <Typography variant="caption" color="text.secondary">
                  The reconciled view for this stay date — one row per room rate, length of stay and occupancy. A
                  room-type or rate-plan search fans out across every room rate underneath it. A blank rate is usually
                  routine; only a genuine gap is flagged.
                </Typography>
              </Box>
              {stateRows.length === 0 ? (
                <Box p={3}>
                  <Typography variant="body2" color="text.secondary">
                    No reconciled state for this stay date.
                  </Typography>
                </Box>
              ) : (
                <AriStateTable rows={stateRows} />
              )}
            </>
          )}
        </Paper>
      )}

      <AriPayloadDialog row={payloadRow} onClose={() => setPayloadRow(null)} />

      {!hasSearched && (
        <Typography variant="body2" color="text.secondary">
          Enter a room rate, room type or rate plan ID and a stay date to see what the channel manager sent us.
        </Typography>
      )}
    </Box>
  );
}
